import React from 'react';
import { Box, Switch, Typography } from '@mui/material';
import { SlideObject } from '../types';
import { PRES, TYPE } from '../chrome';
import { COPY } from '../presentationCopy';
import { PanelRow } from '../ui';
import { SliderRow } from './ToolbarFlyout';
import ColorField from './ColorField';
import { canUseShadowSpread, DEFAULT_SHADOW, resolvedShadow } from './objectEffects';

export function canRoundObject(object: SlideObject): boolean {
  if (object.type === 'text' || object.type === 'image' || object.type === 'product') return true;
  const shape = object.style?.shape || 'rect';
  return object.type === 'shape' && (shape === 'rect' || shape === 'roundRect');
}

export function canSpreadObject(object: SlideObject): boolean {
  return object.type === 'text' || (object.type === 'shape' && object.style?.shape !== 'line');
}

export function resolvedCornerRadius(object: SlideObject): number {
  if (object.style?.cornerRadius != null) return object.style.cornerRadius;
  if (object.type === 'shape' && object.style?.shape === 'roundRect') {
    return Math.round(Math.min(object.w, object.h) * 0.12);
  }
  return 0;
}

interface EffectsControlsProps {
  object: SlideObject;
  disabled?: boolean;
  onStyle: (patch: Record<string, unknown>) => void;
}

const EffectsControls: React.FC<EffectsControlsProps> = ({ object, disabled, onStyle }) => {
  const showRadius = canRoundObject(object);
  const showSpread = canSpreadObject(object);
  const maxRadius = Math.max(80, Math.round(Math.min(object.w, object.h) / 2));
  const shadow = resolvedShadow(object.style);
  const showShadowSpread = canUseShadowSpread(object);

  return (
    <Box>
      {showRadius && (
        <PanelRow label={COPY.cornerRadius} stacked>
          <SliderRow
            disabled={disabled}
            value={resolvedCornerRadius(object)}
            min={0}
            max={maxRadius}
            onChange={(value) => onStyle({ cornerRadius: value })}
          />
        </PanelRow>
      )}
      {showSpread && (
        <PanelRow label="Spridning" stacked>
          <SliderRow
            disabled={disabled}
            value={object.style?.padding ?? 0}
            min={0}
            max={80}
            onChange={(value) => onStyle({ padding: value })}
          />
        </PanelRow>
      )}
      <PanelRow label={COPY.shadow}>
        <Switch
          size="small"
          disabled={disabled}
          checked={shadow.enabled}
          onChange={(event) => onStyle({
            shadow: { ...DEFAULT_SHADOW, ...shadow, enabled: event.target.checked },
          })}
        />
      </PanelRow>
      {shadow.enabled && (
        <>
          <ColorField label="Skuggfärg" value={shadow.color} disabled={disabled} onChange={(color) => onStyle({ shadow: { ...shadow, enabled: true, color } })} />
          <PanelRow label="Opacitet" stacked>
            <SliderRow
              disabled={disabled}
              value={Math.round(shadow.opacity * 100)}
              min={0}
              max={100}
              onChange={(value) => onStyle({ shadow: { ...shadow, enabled: true, opacity: value / 100 } })}
            />
          </PanelRow>
          <PanelRow label="Vinkel" stacked>
            <SliderRow
              disabled={disabled}
              value={shadow.angle}
              min={0}
              max={360}
              onChange={(value) => onStyle({ shadow: { ...shadow, enabled: true, angle: value } })}
            />
          </PanelRow>
          <PanelRow label="Avstånd" stacked>
            <SliderRow
              disabled={disabled}
              value={shadow.distance}
              min={0}
              max={80}
              onChange={(value) => onStyle({ shadow: { ...shadow, enabled: true, distance: value } })}
            />
          </PanelRow>
          <PanelRow label="Oskärpa" stacked>
            <SliderRow
              disabled={disabled}
              value={shadow.blur}
              min={0}
              max={80}
              onChange={(value) => onStyle({ shadow: { ...shadow, enabled: true, blur: value } })}
            />
          </PanelRow>
          {showShadowSpread && (
            <PanelRow label="Spridning skugga" stacked>
              <SliderRow
                disabled={disabled}
                value={shadow.spread}
                min={0}
                max={40}
                onChange={(value) => onStyle({ shadow: { ...shadow, enabled: true, spread: value } })}
              />
            </PanelRow>
          )}
        </>
      )}
      <PanelRow label={COPY.multiply} stacked>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.caption, color: PRES.textTertiary }}>
            {COPY.multiplyHelp}
          </Typography>
          <Switch
            size="small"
            disabled={disabled}
            checked={object.style?.blendMode === 'multiply'}
            onChange={(event) => onStyle({ blendMode: event.target.checked ? 'multiply' : 'normal' })}
          />
        </Box>
      </PanelRow>
    </Box>
  );
};

export default EffectsControls;
